import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '../Dialog/Dialog.tsx';
import styles from './CommandPalette.module.css';

export type Command = { id: string; label: string; keywords?: readonly string[]; run: () => void };

const isShortcut = (event: KeyboardEvent): boolean =>
  (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';

const APPLE_PLATFORM = /Mac|iPhone|iPad/u;

const shortcutLabel = (): string => (APPLE_PLATFORM.test(navigator.userAgent) ? '⌘ K' : 'Ctrl K');

const matches = (command: Command, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  return [command.label, ...(command.keywords ?? [])].some((text) => text.toLowerCase().includes(needle));
};

export type CommandGroup = {
  readonly title: string;
  readonly commands: readonly Command[];
  readonly busy: boolean;
};

export type CommandPaletteProps = {
  commands: readonly Command[];
  labels: { trigger: string; placeholder: string; title: string; empty: string; busy?: string };
  found?: CommandGroup | undefined;
  onQueryChange?: ((query: string) => void) | undefined;
};

export const CommandPalette = ({ commands, labels, found, onQueryChange }: CommandPaletteProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isShortcut(event)) {
        event.preventDefault();
        setOpen((current) => !current);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const close = () => {
    setOpen(false);
  };

  const visible = useMemo(() => commands.filter((command) => matches(command, query)), [commands, query]);

  return (
    <>
      <button
        type="button"
        className={styles['trigger']}
        onClick={() => {
          setOpen(true);
        }}
      >
        <Search size={16} aria-hidden="true" />
        <span className={styles['triggerText']}>{labels.trigger}</span>
        <kbd className={styles['shortcut']}>{shortcutLabel()}</kbd>
      </button>
      <Dialog
        open={open}
        title={labels.title}
        onClose={() => {
          setOpen(false);
        }}
      >
        <input
          ref={inputRef}
          className={styles['input']}
          placeholder={labels.placeholder}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            onQueryChange?.(event.target.value);
          }}
          aria-label={labels.placeholder}
        />
        {found === undefined || (found.commands.length === 0 && !found.busy) ? null : (
          <section aria-label={found.title} className={styles['group']}>
            <h3 className={styles['groupTitle']}>{found.title}</h3>
            {found.busy ? (
              <p className={styles['busy']} role="status">
                {labels.busy}
              </p>
            ) : null}
            <CommandList commands={found.commands} onRun={close} />
          </section>
        )}
        {visible.length === 0 && (found === undefined || found.commands.length === 0) ? (
          <p className={styles['busy']}>{labels.empty}</p>
        ) : (
          <CommandList commands={visible} onRun={close} />
        )}
      </Dialog>
    </>
  );
};

const CommandList = ({ commands, onRun }: { commands: readonly Command[]; onRun: () => void }) => (
  <ul className={styles['results']}>
    {commands.map((command) => (
      <li key={command.id}>
        <button
          type="button"
          className={styles['result']}
          onClick={() => {
            command.run();
            onRun();
          }}
        >
          {command.label}
        </button>
      </li>
    ))}
  </ul>
);
